/**
 * Frontend API audit — switches from config/csrp-logging.json (loaded at startup).
 */
import { getCsrpLoggingConfig } from "./csrpLoggingConfig";

const SUCCESS_KEY_PREFIX = "csrp_api_success_";
const FAILURE_KEY_PREFIX = "csrp_api_failures_";
const MAX_SUCCESS_ENTRIES = 150;
const MAX_FAILURE_ENTRIES = 200;
const MAX_BODY_CHARS = 4000;

const SENSITIVE_KEYS = new Set([
  "password",
  "passwd",
  "pwd",
  "token",
  "accesstoken",
  "refreshtoken",
  "authorization",
  "secret",
  "apikey",
]);

export const isFrontEndApiSuccessLogEnabled = () => {
  const c = getCsrpLoggingConfig();
  return c.enabled && c.logSuccess;
};

export const isFrontEndApiFailureLogEnabled = () => {
  const c = getCsrpLoggingConfig();
  return c.enabled && c.logFailure;
};

/** True if either success or failure logging is on. */
export const isApiClientLogEnabled = () =>
  isFrontEndApiSuccessLogEnabled() || isFrontEndApiFailureLogEnabled();

/** Optional hints for debugging; server + FrontEndLog files use config/csrp-logging.json only. */
export const getClientLogPreferenceHeaders = () => ({
  "X-Csrp-Client-Log-Success": isFrontEndApiSuccessLogEnabled() ? "true" : "false",
  "X-Csrp-Client-Log-Failure": isFrontEndApiFailureLogEnabled() ? "true" : "false",
});

/** Attach log preference headers (axios 1.x-safe). */
export const applyClientLogHeaders = (config) => {
  const prefs = getClientLogPreferenceHeaders();
  if (!config.headers) {
    config.headers = {};
  }
  if (typeof config.headers.set === "function") {
    Object.entries(prefs).forEach(([key, value]) => config.headers.set(key, value));
  } else {
    Object.assign(config.headers, prefs);
  }
};

/** Session lines are server-side only — not controlled by SUCCESS/FAILURE. */
export const isSessionClientLogEnabled = () => false;

const utcDayKey = () => new Date().toISOString().slice(0, 10);

const shouldSkipClientLog = (config) => {
  if (config?.__skipClientLog) return true;
  const u = String(config?.url || "").toLowerCase();
  return (
    u.includes("/api/diagnostics/") ||
    u.includes("/api/session/keepalive") ||
    u.includes("/api/auth/login") ||
    u.includes("/api/auth/logout")
  );
};

const trimTrailingSlash = (s) => String(s || "").replace(/\/+$/, "");

/** From config/csrp-logging.json — sent to API as X-Csrp-FrontEnd-Log-Root. */
export const getFrontEndLogRootPath = () =>
  String(getCsrpLoggingConfig().frontEndLogPath || "frontend/FrontEndLog").trim();

const clientLogRequestHeaders = () => {
  const root = getFrontEndLogRootPath();
  if (!root) return { "Content-Type": "application/json" };
  return {
    "Content-Type": "application/json",
    "X-Csrp-FrontEnd-Log-Root": root,
  };
};

/** Payload for POST /api/diagnostics/client-api-success|failure → FrontEndLog/ */
export const buildClientLogServerPayload = (entry) => {
  const request = entry.request || {};
  const response = entry.response || {};
  return {
    kind: entry.kind,
    atUtcIso: request.tsUtc || entry.savedAt,
    savedAt: entry.savedAt,
    method: request.method,
    url: request.url,
    requestId: entry.requestId,
    httpStatus: response.httpStatus,
    ms: response.ms ?? null,
    message: response.message || "",
    code: response.code || null,
    isTimeout: response.isTimeout === true,
    isNetwork: response.isNetwork === true,
    isSessionExpired: response.isSessionExpired === true,
    reason: entry.reason ?? null,
    requestBody: request.body,
    responseBody: response.body,
  };
};

/**
 * Session lifecycle only (login / logout / expiry) — not keep-alive.
 * @param {"session-start"|"session-end"} event
 * @param {string} reason login | logout | expired | unauthorized | client_session_expired
 */
export async function logSessionEvent(event, reason, getApiBaseUrl, userId = null) {
  if (!isSessionClientLogEnabled()) return;
  const body = {
    event,
    reason,
    userId: userId || getClientUserId(),
    atUtcIso: new Date().toISOString(),
  };
  try {
    const base = trimTrailingSlash(getApiBaseUrl() || "");
    await fetch(`${base}/api/diagnostics/client-session-event`, {
      method: "POST",
      headers: clientLogRequestHeaders(),
      credentials: "include",
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    // ignore
  }
  if (isSessionClientLogEnabled()) {
    // eslint-disable-next-line no-console
    console.info(`[CSRP SESSION ${event}]`, body);
  }
}

const getClientUserId = () => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    const u = JSON.parse(raw);
    return u?.userIdCode ?? u?.UserIdCode ?? u?.userName ?? u?.UserName ?? null;
  } catch {
    return null;
  }
};

export async function postClientLogToServer(payload, getApiBaseUrl) {
  const urlStr = String(payload.url || "");
  if (urlStr.includes("/api/diagnostics/")) return;

  const isSuccess = payload.kind === "success";
  const path = isSuccess ? "client-api-success" : "client-api-failure";

  try {
    const base = trimTrailingSlash(getApiBaseUrl() || "");
    await fetch(`${base}/api/diagnostics/${path}`, {
      method: "POST",
      headers: clientLogRequestHeaders(),
      credentials: "include",
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // API down — sessionStorage still has the row
  }
}

const redactValue = (key, value) => {
  if (SENSITIVE_KEYS.has(String(key || "").toLowerCase())) {
    return "[redacted]";
  }
  if (value != null && typeof value === "object") {
    return redactObject(value);
  }
  return value;
};

const redactObject = (obj, depth = 0) => {
  if (obj == null || depth > 8) return obj;
  if (typeof FormData !== "undefined" && obj instanceof FormData) {
    return "[FormData]";
  }
  if (Array.isArray(obj)) {
    return obj.map((item) =>
      typeof item === "object" && item != null ? redactObject(item, depth + 1) : item
    );
  }
  if (typeof obj !== "object") return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = redactValue(k, v);
  }
  return out;
};

const clipString = (s, max = MAX_BODY_CHARS) => {
  const str = String(s ?? "");
  if (str.length <= max) return str;
  return `${str.slice(0, max)}…`;
};

export const summarizePayload = (data) => {
  if (data == null) return null;
  try {
    if (typeof FormData !== "undefined" && data instanceof FormData) {
      const parts = [];
      for (const [k] of data.entries()) {
        parts.push(k);
      }
      return clipString(`[FormData keys=${parts.join(",")}]`);
    }
    if (typeof data === "string") return clipString(data);
    const safe = redactObject(data);
    return clipString(JSON.stringify(safe));
  } catch {
    return "[unserializable]";
  }
};

/**
 * @param {import('axios').AxiosRequestConfig} config
 * @param {(cfg: import('axios').AxiosRequestConfig) => string} describeRequestUrl
 */
export const buildRequestLogPart = (config, describeRequestUrl) => {
  const method = String(config.method || "get").toUpperCase();
  const url = describeRequestUrl(config);
  let body = null;
  if (config.data != null) {
    body = summarizePayload(config.data);
  } else if (config.params != null) {
    body = summarizePayload(config.params);
  }
  return {
    tsUtc: new Date().toISOString(),
    method,
    url,
    body,
  };
};

export const buildResponseLogPart = (response, ms) => {
  const status = response?.status ?? null;
  const data = response?.data;
  return {
    tsUtc: new Date().toISOString(),
    httpStatus: status,
    ms: ms ?? null,
    body: summarizePayload(data),
  };
};

export const buildErrorResponseLogPart = (error, ms) => {
  const res = error?.response;
  return {
    tsUtc: new Date().toISOString(),
    httpStatus: res?.status ?? null,
    ms: ms ?? null,
    body: res?.data != null ? summarizePayload(res.data) : null,
    message: clipString(String(error?.message || ""), 2000),
    code: error?.code || null,
    isTimeout: error?.isTimeout === true,
    isNetwork:
      error?.code === "ERR_NETWORK" ||
      String(error?.message || "").includes("Network Error") ||
      res == null,
    isSessionExpired: error?.isSessionExpired === true,
  };
};

const appendToStorage = (prefix, entry, maxEntries) => {
  try {
    const key = prefix + utcDayKey();
    const raw = sessionStorage.getItem(key);
    const arr = raw ? JSON.parse(raw) : [];
    arr.push(entry);
    while (arr.length > maxEntries) arr.shift();
    sessionStorage.setItem(key, JSON.stringify(arr));
  } catch {
    // quota / private mode
  }
};

const consoleLog = (label, payload, { success = true } = {}) => {
  if (success && !isFrontEndApiSuccessLogEnabled()) return;
  if (!success && !isFrontEndApiFailureLogEnabled()) return;
  // eslint-disable-next-line no-console
  console.info(label, payload);
};

/**
 * Call from axios request interceptor after URL is resolved.
 */
export const logOutgoingApiRequest = (config, describeRequestUrl) => {
  if (shouldSkipClientLog(config)) return;
  const request = buildRequestLogPart(config, describeRequestUrl);
  config.__csrpClientLogRequest = request;

  if (isFrontEndApiSuccessLogEnabled()) {
    consoleLog(
      "[CSRP API REQUEST]",
      { requestId: config.__csrpReqId || null, ...request },
      { success: true }
    );
  }
};

/**
 * Call from axios success response interceptor.
 */
export const logIncomingApiSuccess = (response, describeRequestUrl, ms, getApiBaseUrl) => {
  if (!isFrontEndApiSuccessLogEnabled()) return;
  const cfg = response?.config || {};
  if (shouldSkipClientLog(cfg)) return;
  const url = describeRequestUrl(cfg);

  const request = cfg.__csrpClientLogRequest || buildRequestLogPart(cfg, describeRequestUrl);
  const responsePart = buildResponseLogPart(response, ms);
  const entry = {
    kind: "success",
    requestId: cfg.__csrpReqId || null,
    request,
    response: responsePart,
    savedAt: new Date().toISOString(),
  };

  appendToStorage(SUCCESS_KEY_PREFIX, entry, MAX_SUCCESS_ENTRIES);
  if (typeof getApiBaseUrl === "function") {
    postClientLogToServer(buildClientLogServerPayload(entry), getApiBaseUrl);
  }
  consoleLog(
    "[CSRP API RESPONSE OK]",
    {
      requestId: entry.requestId,
      method: request.method,
      url: request.url,
      httpStatus: responsePart.httpStatus,
      ms: responsePart.ms,
      requestBody: request.body,
      responseBody: responsePart.body,
    },
    { success: true }
  );
};

/**
 * Call from axios error interceptor (and session-expired guard).
 * @returns {object} payload suitable for server client-api-failure report
 */
export const logIncomingApiFailure = (error, describeRequestUrl, ms, getApiBaseUrl) => {
  const cfg = error?.config || {};
  const url = describeRequestUrl(cfg);
  const request =
    cfg.__csrpClientLogRequest ||
    (url ? buildRequestLogPart(cfg, describeRequestUrl) : { tsUtc: new Date().toISOString(), method: "?", url: url || "", body: null });
  const responsePart = buildErrorResponseLogPart(error, ms);

  const entry = {
    kind: "fail",
    requestId: cfg.__csrpReqId || null,
    request,
    response: responsePart,
    savedAt: new Date().toISOString(),
  };

  const reason = responsePart.isSessionExpired
    ? "session_expired"
    : responsePart.isTimeout
      ? "timeout"
      : responsePart.httpStatus != null && responsePart.httpStatus >= 400
        ? "http_error"
        : responsePart.isNetwork
          ? "network"
          : null;

  if (!shouldSkipClientLog(cfg)) {
    if (isFrontEndApiFailureLogEnabled()) {
      appendToStorage(FAILURE_KEY_PREFIX, entry, MAX_FAILURE_ENTRIES);
      if (typeof getApiBaseUrl === "function") {
        const serverPayload = buildClientLogServerPayload({ ...entry, reason });
        postClientLogToServer(serverPayload, getApiBaseUrl);
      }
      consoleLog(
        "[CSRP API RESPONSE FAIL]",
        {
          requestId: entry.requestId,
          method: request.method,
          url: request.url,
          httpStatus: responsePart.httpStatus,
          ms: responsePart.ms,
          message: responsePart.message,
          requestBody: request.body,
          responseBody: responsePart.body,
        },
        { success: false }
      );
    }
  }

  return buildClientLogServerPayload({ ...entry, reason });
};

/** Read today's (or given `yyyy-mm-dd`) log rows from sessionStorage. */
export const getClientApiLogs = (kind = "success", day = utcDayKey()) => {
  try {
    const prefix = kind === "fail" ? FAILURE_KEY_PREFIX : SUCCESS_KEY_PREFIX;
    const raw = sessionStorage.getItem(prefix + day);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const clearClientApiLogs = (kind) => {
  try {
    const day = utcDayKey();
    if (!kind || kind === "success") {
      sessionStorage.removeItem(SUCCESS_KEY_PREFIX + day);
    }
    if (!kind || kind === "fail") {
      sessionStorage.removeItem(FAILURE_KEY_PREFIX + day);
    }
  } catch {
    // ignore
  }
};

/** Attach to `window` in dev for DevTools: `csrpApiLogs('success')` */
export const exposeClientApiLogHelpers = () => {
  if (typeof window === "undefined") return;
  try {
    window.csrpApiLogs = (kind = "success", day) => getClientApiLogs(kind, day);
    window.csrpClearApiLogs = (kind) => clearClientApiLogs(kind);
  } catch {
    // ignore
  }
};

exposeClientApiLogHelpers();
